import React from 'react';
import { X, RotateCcw, Trash2, AlertCircle, CheckCircle, Clock, Play, Copy } from 'lucide-react';
import type { QueueItem, QueueItemStatus } from '../../types';
import ConfirmationDialog from '../common/ConfirmationDialog';

interface QueueItemDetailsModalProps {
  item: QueueItem;
  isOpen: boolean;
  onClose: () => void;
  onRetry?: (itemId: string) => Promise<void>;
  onDelete?: (itemId: string) => Promise<void>;
}

/**
 * QueueItemDetailsModal - Display full details of a queue item
 *
 * AC-435: Queue Items Viewing UI
 */
const QueueItemDetailsModal: React.FC<QueueItemDetailsModalProps> = ({
  item,
  isOpen,
  onClose,
  onRetry,
  onDelete,
}) => {
  const [actionLoading, setActionLoading] = React.useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false);

  if (!isOpen) return null;

  // Get status icon
  const getStatusIcon = (status: QueueItemStatus) => {
    switch (status) {
      case 'pending':
        return <Clock className="w-5 h-5 text-gray-500 dark:text-gray-400" />;
      case 'processing':
        return <Play className="w-5 h-5 text-primary-500" />;
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'failed':
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      default:
        return null;
    }
  };

  // Get status badge class
  const getStatusBadgeClass = (status: QueueItemStatus) => {
    switch (status) {
      case 'pending':
        return 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300';
      case 'processing':
        return 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300';
      case 'completed':
        return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300';
      case 'failed':
        return 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300';
      default:
        return 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300';
    }
  };

  // Handle retry
  const handleRetry = async () => {
    if (!onRetry) return;
    setActionLoading(true);
    try {
      await onRetry(item.item_id);
      onClose();
    } catch (err) {
      console.error('Failed to retry item:', err);
    } finally {
      setActionLoading(false);
    }
  };

  // Handle delete
  const confirmDelete = async () => {
    if (!onDelete) {
      setShowDeleteConfirm(false);
      return;
    }
    setActionLoading(true);
    try {
      await onDelete(item.item_id);
      setShowDeleteConfirm(false);
      onClose();
    } catch (err) {
      console.error('Failed to delete item:', err);
      setShowDeleteConfirm(false);
    } finally {
      setActionLoading(false);
    }
  };

  // Copy payload to clipboard
  const copyPayload = () => {
    navigator.clipboard.writeText(JSON.stringify(item.payload, null, 2));
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div className="relative bg-white dark:bg-cap-dark rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              {getStatusIcon(item.status)}
              <h2 className="ml-2 text-lg font-medium text-gray-900 dark:text-warm-white">Queue Item Details</h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:text-gray-400"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-4 overflow-y-auto max-h-[calc(90vh-140px)]">
            {/* Status */}
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Status</h3>
              <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium ${getStatusBadgeClass(item.status)}`}>
                {getStatusIcon(item.status)}
                <span className="ml-2 capitalize">{item.status}</span>
              </span>
            </div>

            {/* Error Message (if failed) */}
            {item.status === 'failed' && item.error_message && (
              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Error Message</h3>
                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                  <p className="text-sm text-red-700 dark:text-red-300">{item.error_message}</p>
                </div>
              </div>
            )}

            {/* Payload */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Payload</h3>
                <button
                  onClick={copyPayload}
                  className="inline-flex items-center text-xs text-primary-600 hover:text-primary-800"
                >
                  <Copy className="w-3 h-3 mr-1" />
                  Copy
                </button>
              </div>
              <pre className="p-4 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-md text-sm text-gray-800 dark:text-gray-200 overflow-x-auto">
                {JSON.stringify(item.payload, null, 2)}
              </pre>
            </div>

            {/* Details Grid */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Priority</h3>
                <p className="text-sm text-gray-900 dark:text-warm-white">{item.priority}</p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Retry Count</h3>
                <p className="text-sm text-gray-900 dark:text-warm-white">{item.retry_count} / {item.max_retries}</p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Created At</h3>
                <p className="text-sm text-gray-900 dark:text-warm-white">
                  {new Date(item.created_at).toLocaleString()}
                </p>
              </div>
              {item.updated_at && (
                <div>
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Updated At</h3>
                  <p className="text-sm text-gray-900 dark:text-warm-white">
                    {new Date(item.updated_at).toLocaleString()}
                  </p>
                </div>
              )}
              {item.processed_at && (
                <div>
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Processed At</h3>
                  <p className="text-sm text-gray-900 dark:text-warm-white">
                    {new Date(item.processed_at).toLocaleString()}
                  </p>
                </div>
              )}
              <div>
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Item ID</h3>
                <p className="text-xs text-gray-600 dark:text-gray-400 font-mono">{item.item_id}</p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end px-6 py-4 border-t border-gray-200 dark:border-gray-700 space-x-3">
            {item.status === 'failed' && onRetry && (
              <button
                onClick={handleRetry}
                disabled={actionLoading}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-primary-600 dark:text-blue-400 bg-primary-50 dark:bg-blue-900/20 border border-primary-300 dark:border-blue-700 rounded-md hover:bg-primary-100 dark:hover:bg-blue-900/30 disabled:opacity-50"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Retry
              </button>
            )}
            {onDelete && (
              <button
                onClick={() => setShowDeleteConfirm(true)}
                disabled={actionLoading}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-700 rounded-md hover:bg-red-100 dark:hover:bg-red-900/30 disabled:opacity-50"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </button>
            )}
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-cap-dark border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Layered above the details modal via the dialog's own fixed-position root. */}
      {showDeleteConfirm && (
        <ConfirmationDialog
          title="Delete queue item?"
          message="Are you sure you want to delete this item? This action cannot be undone."
          confirmText="Delete"
          confirmVariant="danger"
          isLoading={actionLoading}
          onConfirm={confirmDelete}
          onCancel={() => setShowDeleteConfirm(false)}
        />
      )}
    </div>
  );
};

export default QueueItemDetailsModal;
